"use client";
import { Form } from "@base-ui/react";
import { useMutation } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import ovum from "#/assets/images/ovum.png";
import PageHeader from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import ExtendedImage from "@/components/ui/extended-image";
// biome-ignore lint/performance/noNamespaceImport: <explanation>
import * as Field from "@/components/ui/form/field";
import { OvulationAction } from "../_components/action";
import type { Ovulation, OvulationResponse } from "../_components/types";
import { ResultDialog } from "./_components/result-dialog";

const OvulationCalculate = () => {
  const { control, handleSubmit } = useForm<Ovulation>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [result, setResult] = useState<OvulationResponse | null>(null);

  const mutation = useMutation({
    mutationFn: async (data: Ovulation) => {
      return await OvulationAction(data);
    },
    onSuccess: (resp) => {
      if (resp.isSuccess) {
        setResult(resp.result);
        setDialogOpen(true);
      }
    },
  });

  const onSubmit = (data: Ovulation) => {
    mutation.mutate(data);
  };

  return (
    <div className="flex flex-col gap-4 justify-center mb-20">
      <PageHeader title="Yumurtlama Günü Hesaplama" />
      <div className="rounded-full border border-gray-300 size-56 mx-auto mb-10">
        <ExtendedImage
          alt="ovum"
          className="rounded-full size-56 "
          height={224}
          src={ovum}
          style={{ backgroundColor: "#FF83B1" }}
          width={224}
        />
      </div>
      <Form
        className="max-w-xs mx-auto flex flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          control={control}
          name="lastPeriodStartDate"
          render={({ field, fieldState }) => {
            return (
              <Field.Root
                dirty={fieldState.isDirty}
                invalid={fieldState.invalid}
                name={field.name}
                touched={fieldState.isTouched}
              >
                <Field.Label>Son Adet Tarihinizin İlk Günü</Field.Label>
                <Field.Control
                  className="w-full"
                  onBlur={field.onBlur}
                  onValueChange={field.onChange}
                  ref={field.ref}
                  type="date"
                  value={field.value}
                />
                <Field.Error match={!!fieldState.error}>
                  {fieldState.error?.message}
                </Field.Error>
              </Field.Root>
            );
          }}
        />
        <Controller
          control={control}
          name="previousPeriodStartDate"
          render={({ field, fieldState }) => {
            return (
              <Field.Root
                dirty={fieldState.isDirty}
                invalid={fieldState.invalid}
                name={field.name}
                touched={fieldState.isTouched}
              >
                <Field.Label>Bir Önceki Kanamanın İlk Günü</Field.Label>
                <Field.Control
                  onBlur={field.onBlur}
                  onValueChange={field.onChange}
                  ref={field.ref}
                  type="date"
                  value={field.value}
                />
                <Field.Error match={!!fieldState.error}>
                  {fieldState.error?.message}
                </Field.Error>
              </Field.Root>
            );
          }}
        />
        <div className="flex flex-row gap-2 bg-gray-100 rounded-xl p-2 justify-start items-center">
          <Info className="size-5 shrink-0 text-gray-500" />
          <p className="text-[10px] leading-3.5 font-medium text-gray-600">
            Yalnızca son adet tarihinizi girdiyseniz, adet döngünüz otomatik
            olarak 28 gün kabul edilecektir. Gerçek tarihlerle yapılan
            hesapların daha güvenilir olduğunu hatırlatmak isteriz.
          </p>
        </div>
        <Button disabled={mutation.isPending} type="submit">
          {mutation.isPending ? "Hesaplanıyor..." : "Hesapla"}
        </Button>
      </Form>
      <ResultDialog
        onOpenChange={setDialogOpen}
        open={dialogOpen}
        result={result}
      />
    </div>
  );
};

export default OvulationCalculate;
